import type { FunctionComponent } from "preact";
import Separator from "./components/Separator.tsx";
import useApi, { type Collection, type TestPreview } from "./tests-api.ts";
import OffsetFetch from "./components/OffsetFetch.tsx";
import * as stylex from "@stylexjs/stylex";

import placeholderPic from "./assets/image-placeholder.png";
import NiceModal from "@ebay/nice-modal-react";
import TestApplet from "./TestApplet.tsx";
import { useQueryClient } from "@tanstack/preact-query";

const styles = stylex.create({
    testCatalog: {
        gap: 20,
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, 130px)",
    },
    testEntry: {
        gap: "8px 12px",
        display: "flex",
        flexDirection: "column",
        padding: 15,
        cursor: "pointer",
        color: "#444",
        textAlign: "center",
        alignItems: "center",
        borderRadius: 5,
        fontSize: 13,
        fontFamily: "'Inter Variable', sans-serif",
        backgroundColor: {
            default: "transparent",
            ":hover": "#EEE",
        },
    },
    testIcon: {
        width: 72,
        objectFit: "cover",
        boxShadow: "color-mix(in prophoto-rgb, rgb(0, 0, 0) 20%, transparent) 0px 0px 15px 2px",
        aspectRatio: 1,
        borderRadius: 4,
        backgroundColor: "#dae4ed",
        backgroundSize: "contain",
        color: "transparent" /* hide alts during loading */,
    },
});

const CatalogFragment: FunctionComponent<{
    popular: boolean;
    openTest: (id: TestPreview) => void;
}> = ({ popular, openTest }) => {
    return (
        <OffsetFetch
            queryKey={["tests", popular ? "popular" : "new"]}
            queryFn={async (offset: string): Promise<Collection<TestPreview>> =>
                await (await useApi()).getTests(popular ? "popular" : "new", offset)
            }
            item={(test) => (
                <div {...stylex.props(styles.testEntry)} onClick={() => openTest(test)}>
                    <img
                        {...stylex.props(styles.testIcon)}
                        key={test.icon} /* Needed for immediate update */
                        src={test.icon as string}
                        alt={test.title}
                        draggable={false}
                        style={{ backgroundImage: `url(${placeholderPic})` }}
                    />
                    <span>{test.title}</span>
                </div>
            )}
            Wrapper={({ children }) => <div {...stylex.props(styles.testCatalog)}>{children}</div>}
        />
    );
};

const Catalog: FunctionComponent = () => {
    const queryClient = useQueryClient();
    const openTest = (test: TestPreview) => {
        NiceModal.show(TestApplet, {
            testPreview: test,
            acquireFullTest: async () => {
                try {
                    return await (await useApi()).getTest(test.id, false, true);
                } finally {
                    await queryClient.invalidateQueries({ queryKey: ["accountDetails"] });
                }
            },
        });
    };

    return (
        <>
            <Separator label="Популярные тесты">
                <CatalogFragment popular={true} openTest={openTest} />
            </Separator>
            <Separator label="Новые тесты">
                <CatalogFragment popular={false} openTest={openTest} />
            </Separator>
        </>
    );
};

export default Catalog;
